import { uniqBy } from 'lodash';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Team } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { useResourceManager } from 'components/organisms/resource-management/managers';
import {
  ProjectsWithMembers,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import {
  AllocatedMembers,
  saveTemplatePlaceholder,
} from 'data/templates/projects';
import { updateAllocatedMembersAction } from 'data/templates/projects/actions';
import { getCreatePlaceholderPayload } from 'queries/placeholder';
import { floatUid } from 'utils/uid';

interface Props {
  schedulerRef: SchedulerReference;
  project: ProjectsWithMembers;
  allocatedMembers: AllocatedMembers;
  addResource: ReturnType<typeof useResourceManager>['addResource'];
}
interface Result {
  addTeamsModalProjectId?: number;
  showAddTeamsModal: () => void;
  hideAddTeamsModal: () => void;
  onTeamsAdded: (projectId: number, teams: Team[]) => void;
  onTeamCreate: (values) => Promise<Team>;
}

export const useAddTeamsManager = ({
  schedulerRef,
  addResource,
  allocatedMembers,
  project,
}: Props): Result => {
  const dispatch = useDispatch();
  const [addTeamToProjectId, setAddTeamToProjectId] = useState<number>();
  const showAddTeamsModal = useCallback(() => {
    setAddTeamToProjectId(project.projectId);
  }, [project.projectId]);
  const hideAddTeamsModal = useCallback(() => {
    setAddTeamToProjectId(undefined);
  }, []);
  const addTeamsToState = useCallback(
    (teams: Team[]) => {
      dispatch(
        updateAllocatedMembersAction({
          users: allocatedMembers.users,
          teams: uniqBy(allocatedMembers.teams.concat(teams), 'teamId'),
        }),
      );
    },
    [allocatedMembers.teams, allocatedMembers.users, dispatch],
  );
  const onTeamsAdded = useCallback(
    (projectId: number, _teams: Team[]) => {
      addTeamsToState(_teams);
      schedulerRef.current.instance.suspendRefresh();
      _teams.forEach((team) =>
        addResource({ projectId, resource: { type: ResourceType.Team, team } }),
      );
      schedulerRef.current.instance.resumeRefresh(true);
      setAddTeamToProjectId(undefined);
    },
    [addResource, addTeamsToState, schedulerRef],
  );
  const onTeamCreate = useCallback(
    (values) =>
      new Promise<Team>((resolve) => {
        const team = {
          teamId: floatUid(),
          ...getCreatePlaceholderPayload(values),
        };

        dispatch(saveTemplatePlaceholder(team));
        resolve(team);
      }),
    [dispatch],
  );

  return {
    addTeamsModalProjectId: addTeamToProjectId,
    showAddTeamsModal,
    hideAddTeamsModal,
    onTeamsAdded,
    onTeamCreate,
  };
};
